/**
 * Agrupamento de despesas por categoria — util partilhado entre
 * AdminFinance.js (FinanceDrillDown) e AdminFinancialCharts.js
 * (KpiDetailModal).
 *
 * Backend `/admin/finance/drill` devolve items num formato consistente:
 *   { label, sub, value, negative?, value_display?, kind?, invoice_id?, line_id? }
 * onde `sub` contém a categoria (ex: "2026-08-19 · fuel",
 *      "2026-08 · FTR-000123 · produto · IVA 22%"). Este util extrai a
 * categoria de cada item e devolve os grupos ordenados por total desc.
 */

// Mapa canónico de categorias → label PT. Espelha exactamente o que
// AdminFinance.js expõe para o dropdown. Mantido aqui para o util ser
// self-contained.
export const CATEGORY_LABELS = {
  // Operacionais
  cleaning_products:   "Produtos de limpeza",
  cleaning_material:   "Material de limpeza",
  guest_consumables:   "Consumíveis para clientes",
  laundry:             "Lavandaria",
  equipment:           "Equipamento e ferramentas",
  // Veículos
  fuel:                "Combustível",
  vehicle_maintenance: "Manutenção de veículos",
  tolls_parking:       "Portagens e estacionamento",
  vehicle_insurance:   "Seguros de veículos",
  // Pessoal
  payroll:             "Salários e ordenados",
  social_security:     "Segurança Social",
  training:            "Formação da equipa",
  uniforms_ppe:        "Fardas / EPI",
  // Estrutura
  rent:                "Renda / arrendamento",
  utilities:           "Eletricidade, água, comunicações",
  software:            "Software e subscrições",
  accounting:          "Contabilidade / serviços profissionais",
  marketing:           "Marketing e publicidade",
  banking:             "Bancos e comissões",
  // Impostos e outros
  tax:                 "IVA e impostos",
  maintenance:         "Manutenção geral",
  other:               "Outro / miscelâneas",
  // Buckets auto-gerados por outros fluxos
  supplies:            "Material de limpeza",   // legacy alias
  produtos:            "Produtos (Facturalusa)",
  materiais:           "Materiais (Facturalusa)",
  stripe_fees:         "Comissões Stripe",
};

// Tokens (lowercase) que sinalizam a categoria dentro do `sub`. Precomputamos
// para evitar construir uma RegExp por item.
const CATEGORY_KEYS = Object.keys(CATEGORY_LABELS);
const CATEGORY_KEY_SET = new Set(CATEGORY_KEYS.map((k) => k.toLowerCase()));

/**
 * Extrai a categoria de um item do drill-down.
 * Estratégia: split `sub` pelo separador visual `·`, cada token é limpo
 * (trim + lowercase) e tentamos casar com uma chave conhecida.
 * Adicionalmente, tokens "produto"/"material" (linhas de facturas de stock)
 * são mapeados para os buckets "produtos"/"materiais".
 *
 * `fallback` — chave a devolver quando nenhum token bater. Útil para os
 * drills kind='produtos'/'materiais' cujo payload não repete o token da
 * categoria dentro do `sub`.
 */
export function extractCategory(item, fallback = "other") {
  const sub = (item && item.sub) || "";
  if (!sub) return fallback;
  const tokens = sub.split("·").map((t) => t.trim().toLowerCase());
  for (const t of tokens) {
    if (CATEGORY_KEY_SET.has(t)) return t;
    if (t === "produto") return "produtos";
    if (t === "material") return "materiais";
  }
  return fallback;
}

/**
 * Agrupa items por categoria. Devolve array ordenado por total desc:
 *   [{ category, label, total, items: [...] }, ...]
 *
 * `fallback` propaga para `extractCategory` — por ex., quando o drill kind
 * é 'produtos' passamos fallback='produtos' para os items sem token bater.
 */
export function groupByCategory(items, fallback = "other") {
  const acc = new Map();
  for (const it of items || []) {
    const cat = extractCategory(it, fallback);
    const g = acc.get(cat) || {
      category: cat,
      label: CATEGORY_LABELS[cat] || cat,
      total: 0,
      items: [],
    };
    g.total += Number(it.value || 0);
    g.items.push(it);
    acc.set(cat, g);
  }
  return Array.from(acc.values()).sort((a, b) => b.total - a.total);
}

/**
 * Kinds de drill-down que fazem sentido agrupar por categoria.
 */
export const EXPENSE_DRILL_KINDS = new Set([
  "expenses", "despesas", "produtos", "materiais",
]);
